import React, { useState } from 'react';
import { FadeIn, StaggerContainer, StaggerItem, CTAButton } from './UI';
import { Clock, AlertCircle, XCircle, CheckCircle2, BookOpen, Map, UtensilsCrossed, MessageCircle, ListPlus, Download, PlayCircle, Heart, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function Hero() {
  return <section className="pt-20 pb-16 md:pt-28 md:pb-24 px-4 max-w-6xl mx-auto" data-id="element-70">
      <div className="flex flex-col lg:flex-row items-center gap-12" data-id="element-71">
        <FadeIn className="flex-1 text-center lg:text-left" direction="right" data-id="element-72">
          <div className="inline-block bg-brand-red/10 text-brand-red font-semibold px-4 py-1.5 rounded-full text-sm mb-6" data-id="element-73">
            Para padres de niños de 2 a 10 años
          </div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight text-brand-dark mb-6" data-id="element-74">
            Cenas Sin Batallas: El Método de 15 Minutos para que tus Hijos Coman
            sin Gritos
          </h1>
          <p className="text-lg md:text-xl text-gray-700 mb-10 leading-relaxed" data-id="element-75">
            Transforma el momento más estresante del día en un espacio de paz,
            incluso si tu hijo rechaza todo lo que no sean patatas fritas o
            pasta.
          </p>
          <CTAButton onClick={() => document.getElementById('pricing')?.scrollIntoView({
          behavior: 'smooth'
        })} data-id="element-76" />
        </FadeIn>
        <FadeIn className="flex-1 w-full max-w-lg lg:max-w-none" direction="left" delay={0.2} data-id="element-77">
          <div className="relative" data-id="element-78">
            <div className="absolute inset-0 bg-brand-green/20 rounded-full blur-3xl transform scale-90 -z-10" data-id="element-79"></div>
            <img src="https://cdn.magicpatterns.com/uploads/4hVPnFUU8UUjC2ZkrgRWfk/Cenas_sin_Batallas.png" alt="Cenas Sin Batallas - Guía Digital" className="w-full h-auto object-contain drop-shadow-2xl" data-id="element-80" />
          </div>
        </FadeIn>
      </div>
    </section>;
}
export function Story() {
  return <section className="py-20 bg-white px-4" data-id="element-81">
      <div className="max-w-3xl mx-auto" data-id="element-82">
        <FadeIn data-id="element-83">
          <div className="flex justify-center mb-8" data-id="element-84">
            <Clock className="w-12 h-12 text-brand-red opacity-80" data-id="element-85" />
          </div>
          <div className="prose prose-lg md:prose-xl text-gray-700 mx-auto" data-id="element-86">
            <p className="font-serif text-2xl md:text-3xl leading-relaxed text-brand-dark text-center mb-8" data-id="element-87">
              "Son las 8:15 de la tarde. Has trabajado todo el día, has limpiado
              la casa y has cocinado con la esperanza de que 'esta vez sí
              funcione'."
            </p>
            <p data-id="element-88">
              Pones el plato en la mesa y, antes de que se siente, escuchas la
              frase que te hunde:{' '}
              <strong data-id="element-89">"¡No quiero eso, tiene cosas verdes!"</strong>.
            </p>
            <p data-id="element-90">
              Sientes la rabia subir por tu cuello. Empiezan los sobornos ("tres
              bocados y hay postre"), las amenazas ("si no comes, no hay
              dibujos") y el agotamiento final.
            </p>
            <p className="font-medium text-brand-dark bg-brand-cream p-6 rounded-xl border-l-4 border-brand-red mt-8" data-id="element-91">
              Terminas el día sintiéndote una mala madre o un mal padre,
              cuestionándote por qué algo tan natural como cenar se ha
              convertido en una guerra psicológica.
            </p>
          </div>
        </FadeIn>
      </div>
    </section>;
}
export function Problem() {
  return <section className="py-20 px-4 bg-brand-cream" data-id="element-92">
      <div className="max-w-4xl mx-auto" data-id="element-93">
        <FadeIn data-id="element-94">
          <h2 className="font-serif text-3xl md:text-4xl text-center text-brand-dark mb-12" data-id="element-95">
            El problema no es el brócoli, ni el pescado, ni el puré.{' '}
            <span className="text-brand-red" data-id="element-96">El problema es la presión.</span>
          </h2>
        </FadeIn>

        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-97">
          <FadeIn direction="right" data-id="element-98">
            <div className="space-y-6 text-lg text-gray-700" data-id="element-99">
              <p data-id="element-100">
                Cada vez que obligas, escondes verduras o negocias bocados, el
                cuerpo de tu hijo entra en modo de{' '}
                <strong data-id="element-101">"vuelo o lucha"</strong>. Científicamente, su apetito se
                cierra.
              </p>
              <p data-id="element-102">
                Estás atrapada en un ciclo de "comidas seguras" (pasta, nuggets,
                pan) porque tienes miedo de que se despierte con hambre a
                medianoche.
              </p>
              <p className="font-medium text-brand-dark" data-id="element-103">
                Pero cuanto más cedas o más presiones, más selectivo y difícil
                se vuelve su paladar. Estás agotada de ser una "chef de pedidos
                cortos" en tu propia casa.
              </p>
            </div>
          </FadeIn>
          <FadeIn direction="left" delay={0.2} data-id="element-104">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-105">
              <AlertCircle className="w-10 h-10 text-brand-red mb-4" data-id="element-106" />
              <h3 className="font-serif text-2xl mb-4" data-id="element-107">El Ciclo del Estrés</h3>
              <ul className="space-y-4" data-id="element-108">
                <li className="flex gap-3 text-gray-700" data-id="element-109">
                  <div className="w-6 h-6 rounded-full bg-brand-red/10 text-brand-red flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-110">
                    1
                  </div>
                  <span data-id="element-111">Ofreces comida nueva con esperanza</span>
                </li>
                <li className="flex gap-3 text-gray-700" data-id="element-112">
                  <div className="w-6 h-6 rounded-full bg-brand-red/10 text-brand-red flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-113">
                    2
                  </div>
                  <span data-id="element-114">El niño rechaza y se cierra</span>
                </li>
                <li className="flex gap-3 text-gray-700" data-id="element-115">
                  <div className="w-6 h-6 rounded-full bg-brand-red/10 text-brand-red flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-116">
                    3
                  </div>
                  <span data-id="element-117">Presionas, negocias o te enfadas</span>
                </li>
                <li className="flex gap-3 text-gray-700" data-id="element-118">
                  <div className="w-6 h-6 rounded-full bg-brand-red/10 text-brand-red flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-119">
                    4
                  </div>
                  <span data-id="element-120">Terminas cocinando pasta otra vez</span>
                </li>
              </ul>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>;
}
export function Mistakes() {
  const mistakes = [{
    text: 'Esconder verduras en la salsa crea desconfianza; cuando las detecta, deja de comer incluso lo que antes le gustaba.'
  }, {
    text: "El soborno con el postre enseña que la comida sana es un 'castigo' que hay que sufrir para llegar al premio."
  }, {
    text: "Obligar a 'limpiar el plato' desconecta sus señales naturales de saciedad, fomentando problemas alimentarios a futuro."
  }, {
    text: 'Cocinar un plato diferente para él refuerza que no necesita adaptarse a la comida familiar, perpetuando el problema por años.'
  }];
  return <section className="py-20 px-4 bg-white" data-id="element-121">
      <div className="max-w-4xl mx-auto" data-id="element-122">
        <FadeIn data-id="element-123">
          <h2 className="font-serif text-3xl md:text-4xl text-center text-brand-dark mb-12" data-id="element-124">
            Por qué lo que has intentado hasta ahora no funciona
          </h2>
        </FadeIn>

        <StaggerContainer className="grid md:grid-cols-2 gap-6" data-id="element-125">
          {mistakes.map((mistake, index) => <StaggerItem key={index} data-id="element-126">
              <div className="bg-red-50 p-6 rounded-xl h-full border border-red-100 flex gap-4" data-id="element-127">
                <XCircle className="w-6 h-6 text-brand-red flex-shrink-0 mt-1" data-id="element-128" />
                <p className="text-gray-800 leading-relaxed" data-id="element-129">{mistake.text}</p>
              </div>
            </StaggerItem>)}
        </StaggerContainer>
      </div>
    </section>;
}
export function Solution() {
  return <section className="py-24 px-4 bg-brand-green text-white relative overflow-hidden" data-id="element-130">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none" data-id="element-131">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-white rounded-full blur-3xl" data-id="element-132"></div>
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-white rounded-full blur-3xl" data-id="element-133"></div>
      </div>

      <div className="max-w-4xl mx-auto relative z-10" data-id="element-134">
        <FadeIn data-id="element-135">
          <div className="text-center mb-12" data-id="element-136">
            <span className="uppercase tracking-wider font-semibold text-green-100 text-sm mb-4 block" data-id="element-137">
              El Mecanismo Único
            </span>
            <h2 className="font-serif text-4xl md:text-5xl mb-6" data-id="element-138">
              El Protocolo de Exposición Lúdica Cero Presión
            </h2>
          </div>
        </FadeIn>

        <div className="bg-white/10 backdrop-blur-sm p-8 md:p-10 rounded-2xl border border-white/20" data-id="element-139">
          <FadeIn delay={0.2} data-id="element-140">
            <p className="text-xl md:text-2xl leading-relaxed mb-6 font-medium" data-id="element-141">
              Este método no obliga al niño a tragar, sino que usa la curiosidad
              para que el niño decida probar por voluntad propia.
            </p>
            <p className="text-lg text-green-50 mb-8" data-id="element-142">
              Cambiamos el objetivo: de "tienes que comer" a "vamos a explorar".
              Al eliminar la obligación, el sistema nervioso del niño se relaja.
            </p>
            <div className="bg-white text-brand-dark p-6 rounded-xl flex items-start gap-4" data-id="element-143">
              <UtensilsCrossed className="w-8 h-8 text-brand-green flex-shrink-0" data-id="element-144" />
              <div data-id="element-145">
                <h4 className="font-bold text-lg mb-2" data-id="element-146">
                  El "Plato de Descarte"
                </h4>
                <p className="text-gray-600" data-id="element-147">
                  Usamos una zona de seguridad física que le devuelve el control
                  al niño, reduciendo su ansiedad instantáneamente frente a
                  alimentos nuevos.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>;
}
export function Deliverables() {
  const items = [{
    icon: <BookOpen className="w-6 h-6 text-brand-green" data-id="element-148" />,
    title: 'Guía Maestra Digital',
    desc: 'El paso a paso para desarmar la tensión en la mesa desde la primera noche.'
  }, {
    icon: <Map className="w-6 h-6 text-brand-green" data-id="element-149" />,
    title: 'El Mapa de 7 Días',
    desc: 'Tu hoja de ruta diaria para implementar el método sin errores.'
  }, {
    icon: <UtensilsCrossed className="w-6 h-6 text-brand-green" data-id="element-150" />,
    title: 'Protocolo del Plato de Descarte',
    desc: 'Cómo introducir esta herramienta física para eliminar el miedo a lo nuevo.'
  }, {
    icon: <MessageCircle className="w-6 h-6 text-brand-green" data-id="element-151" />,
    title: 'Guía de Respuestas Rápidas',
    desc: "Qué decir exactamente cuando tu hijo dice '¡Qué asco!', sin perder la paciencia."
  }, {
    icon: <ListPlus className="w-6 h-6 text-brand-green" data-id="element-152" />,
    title: "Lista de 'Alimentos Puente'",
    desc: 'Una lista de texturas y sabores para pasar de lo que ya come a lo que quieres que coma.'
  }];
  return <section className="py-20 px-4 bg-white" data-id="element-153">
      <div className="max-w-5xl mx-auto" data-id="element-154">
        <FadeIn data-id="element-155">
          <h2 className="font-serif text-3xl md:text-4xl text-center text-brand-dark mb-16" data-id="element-156">
            Todo lo que recibes hoy
          </h2>
        </FadeIn>

        <div className="grid md:grid-cols-5 gap-12" data-id="element-157">
          <div className="md:col-span-2 flex items-center justify-center" data-id="element-158">
            <FadeIn direction="right" data-id="element-159">
              <img src="https://cdn.magicpatterns.com/uploads/4hVPnFUU8UUjC2ZkrgRWfk/Cenas_sin_Batallas.png" alt="Contenido del programa" className="w-full max-w-sm drop-shadow-xl" data-id="element-160" />
            </FadeIn>
          </div>

          <div className="md:col-span-3" data-id="element-161">
            <StaggerContainer className="space-y-6" data-id="element-162">
              {items.map((item, index) => <StaggerItem key={index} data-id="element-163">
                  <div className="flex gap-4 p-4 rounded-xl hover:bg-brand-cream transition-colors" data-id="element-164">
                    <div className="w-12 h-12 rounded-full bg-brand-green/10 flex items-center justify-center flex-shrink-0" data-id="element-165">
                      {item.icon}
                    </div>
                    <div data-id="element-166">
                      <h3 className="font-bold text-lg text-brand-dark mb-1" data-id="element-167">
                        {item.title}
                      </h3>
                      <p className="text-gray-600" data-id="element-168">{item.desc}</p>
                    </div>
                  </div>
                </StaggerItem>)}
            </StaggerContainer>
          </div>
        </div>
      </div>
    </section>;
}
export function Audience() {
  return <section className="py-20 px-4 bg-brand-cream" data-id="element-169">
      <div className="max-w-5xl mx-auto" data-id="element-170">
        <div className="grid md:grid-cols-2 gap-8" data-id="element-171">
          <FadeIn direction="right" data-id="element-172">
            <div className="bg-white p-8 rounded-2xl shadow-sm h-full border-t-4 border-brand-green" data-id="element-173">
              <h3 className="font-serif text-2xl mb-6 text-brand-dark flex items-center gap-3" data-id="element-174">
                <CheckCircle2 className="text-brand-green w-8 h-8" data-id="element-175" />
                Esto SÍ es para ti si...
              </h3>
              <ul className="space-y-4" data-id="element-176">
                {['Padres agotados que terminan el día llorando o gritando por la cena.', "Familias con niños de 2 a 10 años que son extremadamente selectivos ('picky eaters').", 'Madres que sienten culpa por cocinar siempre lo mismo para evitar conflictos.', 'Padres que quieren que sus hijos tengan una relación sana y curiosa con la comida.'].map((text, i) => <li key={i} className="flex gap-3 text-gray-700" data-id="element-177">
                    <CheckCircle2 className="w-5 h-5 text-brand-green flex-shrink-0 mt-0.5" data-id="element-178" />
                    <span data-id="element-179">{text}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>

          <FadeIn direction="left" delay={0.2} data-id="element-180">
            <div className="bg-white p-8 rounded-2xl shadow-sm h-full border-t-4 border-gray-300" data-id="element-181">
              <h3 className="font-serif text-2xl mb-6 text-gray-500 flex items-center gap-3" data-id="element-182">
                <XCircle className="text-gray-400 w-8 h-8" data-id="element-183" />
                Esto NO es para ti si...
              </h3>
              <ul className="space-y-4" data-id="element-184">
                {["Personas que buscan una 'solución mágica' de una sola noche sin cambiar su actitud.", 'Casos con patologías médicas o trastornos sensoriales graves que requieren terapia clínica.', 'Padres que prefieren seguir usando el castigo como método de educación.'].map((text, i) => <li key={i} className="flex gap-3 text-gray-500" data-id="element-185">
                    <XCircle className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" data-id="element-186" />
                    <span data-id="element-187">{text}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>;
}
export function HowItWorks() {
  const steps = [{
    icon: <Download className="w-8 h-8 text-brand-red" data-id="element-188" />,
    title: '1. Descarga',
    desc: 'Obtén el acceso inmediato a la guía en tu móvil o tablet.'
  }, {
    icon: <PlayCircle className="w-8 h-8 text-brand-red" data-id="element-189" />,
    title: '2. Implementa',
    desc: "Lee el Capítulo 1 (5 minutos) y aplica la 'Regla de los 2 Minutos' esta misma noche."
  }, {
    icon: <Heart className="w-8 h-8 text-brand-red" data-id="element-190" />,
    title: '3. Disfruta',
    desc: 'Mira cómo el ambiente de tu hogar cambia de una batalla campal a una cena tranquila.'
  }];
  return <section className="py-20 px-4 bg-white" data-id="element-191">
      <div className="max-w-5xl mx-auto" data-id="element-192">
        <FadeIn data-id="element-193">
          <h2 className="font-serif text-3xl md:text-4xl text-center text-brand-dark mb-16" data-id="element-194">
            Cómo funciona
          </h2>
        </FadeIn>

        <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-195">
          <div className="hidden md:block absolute top-12 left-1/6 right-1/6 h-0.5 bg-red-100 z-0" data-id="element-196"></div>

          {steps.map((step, index) => <FadeIn key={index} delay={index * 0.2} className="relative z-10" data-id="element-197">
              <div className="flex flex-col items-center text-center" data-id="element-198">
                <div className="w-24 h-24 rounded-full bg-brand-cream border-4 border-white shadow-md flex items-center justify-center mb-6" data-id="element-199">
                  {step.icon}
                </div>
                <h3 className="font-bold text-xl mb-3" data-id="element-200">{step.title}</h3>
                <p className="text-gray-600" data-id="element-201">{step.desc}</p>
              </div>
            </FadeIn>)}
        </div>
      </div>
    </section>;
}
export function Proof() {
  return <section className="py-16 px-4 bg-brand-dark text-white" data-id="element-202">
      <div className="max-w-4xl mx-auto text-center" data-id="element-203">
        <FadeIn data-id="element-204">
          <TrendingUp className="w-12 h-12 text-brand-green mx-auto mb-6" data-id="element-205" />
          <h2 className="font-serif text-3xl md:text-4xl mb-6" data-id="element-206">
            Resultados Reales
          </h2>
          <p className="text-xl md:text-2xl font-light leading-relaxed mb-8" data-id="element-207">
            En solo 7 días, el{' '}
            <strong className="text-brand-green font-bold" data-id="element-208">
              85% de los padres
            </strong>{' '}
            que aplican la Exposición Lúdica reportan una{' '}
            <strong className="text-brand-green font-bold" data-id="element-209">
              reducción del 90% en los gritos
            </strong>{' '}
            durante la cena.
          </p>
          <p className="text-gray-300 text-lg" data-id="element-210">
            No se trata de que coman ensalada el primer día, se trata de que
            vuelvan a sentarse a la mesa con una sonrisa.
          </p>
        </FadeIn>
      </div>
    </section>;
}
export function FAQ() {
  const faqs = [{
    q: '¿Y si realmente se queda con hambre?',
    a: 'La guía te enseña a manejar esto sin ceder a los snacks de medianoche, asegurando que su salud sea lo primero.'
  }, {
    q: '¿Sirve para adolescentes?',
    a: 'Está optimizado para la etapa de neofobia (2-12 años), pero los principios de presión funcionan a cualquier edad.'
  }, {
    q: '¿Tengo que cocinar cosas raras?',
    a: 'No, usas la comida que ya haces para el resto de la familia.'
  }];
  return <section className="py-20 px-4 bg-brand-cream" data-id="element-211">
      <div className="max-w-3xl mx-auto" data-id="element-212">
        <FadeIn data-id="element-213">
          <h2 className="font-serif text-3xl md:text-4xl text-center text-brand-dark mb-12" data-id="element-214">
            Preguntas Frecuentes
          </h2>
        </FadeIn>

        <div className="space-y-4" data-id="element-215">
          {faqs.map((faq, index) => {
          const [isOpen, setIsOpen] = useState(false);
          return <FadeIn key={index} delay={index * 0.1} data-id="element-216">
                <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-id="element-217">
                  <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-5 text-left flex justify-between items-center focus:outline-none" data-id="element-218">
                    <span className="font-bold text-lg text-brand-dark" data-id="element-219">
                      {faq.q}
                    </span>
                    {isOpen ? <ChevronUp className="text-gray-400" data-id="element-220" /> : <ChevronDown className="text-gray-400" data-id="element-221" />}
                  </button>
                  <AnimatePresence data-id="element-222">
                    {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3
                }} data-id="element-223">
                        <div className="px-6 pb-5 text-gray-600" data-id="element-224">{faq.a}</div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </FadeIn>;
        })}
        </div>
      </div>
    </section>;
}
export function Pricing() {
  return <section id="pricing" className="py-24 px-4 bg-white relative" data-id="element-225">
      <div className="max-w-3xl mx-auto text-center" data-id="element-226">
        <FadeIn data-id="element-227">
          <h2 className="font-serif text-4xl md:text-5xl text-brand-dark mb-6" data-id="element-228">
            Empieza esta misma noche
          </h2>
          <p className="text-xl text-gray-600 mb-10" data-id="element-229">
            Recupera la paz de tus noches por menos de lo que cuesta una pizza a
            domicilio. Sin suscripciones, sin pagos ocultos. Solo una solución
            real para un problema diario.
          </p>

          <div className="bg-brand-cream p-8 md:p-12 rounded-3xl border-2 border-brand-red/20 mb-10 relative" data-id="element-230">
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-brand-red text-white px-6 py-2 rounded-full font-bold text-sm tracking-wide uppercase shadow-lg" data-id="element-231">
              Oferta Limitada
            </div>

            <p className="text-gray-500 font-medium mb-2" data-id="element-232">
              Precio Especial de Lanzamiento
            </p>
            <div className="flex justify-center items-start gap-1 mb-8" data-id="element-233">
              <span className="text-3xl font-bold text-brand-dark mt-2" data-id="element-234">$</span>
              <span className="text-7xl font-bold text-brand-dark tracking-tighter" data-id="element-235">
                9.99
              </span>
              <span className="text-xl font-medium text-gray-500 mt-8" data-id="element-236">
                USD
              </span>
            </div>

            <CTAButton data-id="element-237" />
          </div>
        </FadeIn>
      </div>
    </section>;
}